import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  FormGroup,
  Container,
  Row,
  Col,
  Button,
  CardText,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Label
} from 'reactstrap';
import { DashCircle, PlusCircle, Clock, Phone, Search, XCircle } from 'react-bootstrap-icons';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { format, isSameDay, subDays, setHours, getHours, setMinutes, setSeconds } from 'date-fns';
import { enGB, sv, da, nb } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import SweetAlerts from '../../components/SweetAlerts';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import GInput, { Select } from '../../components/Input';
import client from '../../utils/client';
import { getLanguage, getPlusUrl, getTokenData } from '../../utils/helpers';

import 'react-datepicker/dist/react-datepicker.css';
import { Store } from '../../Store';

registerLocale('en', enGB);
registerLocale('sv', sv);
registerLocale('da', da);
registerLocale('no', nb);

const language = getLanguage();
const dateLocale = language;

const midnight = setHours(setMinutes(setSeconds(new Date(), 0), 0), 0);
const midnight2 = setHours(setMinutes(setSeconds(new Date(), 59), 59), 23);

const plusUrl = getPlusUrl();

const Visits = () => {
  const { t } = useTranslation();

  const { state } = useContext(Store);
  const restrictions = state.restrictions?.data;

  const [inProgress, setInProgress] = useState(null);
  const [error, setError] = useState(null);
  const [affinities, setAffinities] = useState(null);
  const [searchUser, setSearchUser] = useState(null);
  const [users, setUsers] = useState(null);
  const [noUsersFound, setNoUsersFound] = useState(false);
  const [success, setSuccess] = useState(null);
  const [nrOfCards, setNrOfCards] = useState(1);
  const [plusTimes, setPlusTimes] = useState(null);
  const [visits, setVisits] = useState(null);
  const [date, setDate] = useState();
  const [deleteVisit, setDeleteVisit] = useState(null);

  const tokenData = getTokenData();

  const {
    handleSubmit,
    setValue,
    // getValues,
    formState: { errors },
    register,
    trigger,
    reset,
    watch
  } = useForm({
    mode: 'onBlur',
    defaultValues: {}
  });

  const golfclubId = watch('golfclub');

  const {
    handleSubmit: handleSubmitSearch,
    formState: { errors: errorsSearch },
    register: registerSearch
  } = useForm({
    mode: 'onBlur'
  });

  // Do not combine this two useEffects
  useEffect(() => {
    setAffinities([
      { id: tokenData?.gId, name: tokenData?.name },
      ...(tokenData?.affinities || [])
    ]);
  }, [tokenData?.gId]);

  useEffect(() => {
    setValue('golfclub', tokenData?.gId, { shouldValidate: true });
  }, [tokenData?.gId, affinities]);

  useEffect(() => {
    if (golfclubId) {
      fetchPlusTimes();
      fetchVisits();
    }
  }, [golfclubId]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change') return;

      if (name.substr(0, 10) === 'cardNumber') {
        const cardNumber = value[name];
        if (cardNumber.length === 8) {
          trigger(name);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (!success) {
      // const gc = getValues('golfclub');
      // reset({ golfclub: gc });
      reset();
      // resetField('golfclub', { defaultValue: gc });
    }
  }, [success]);

  const fetchPlusTimes = async (teeTime) => {
    setPlusTimes(null);
    setInProgress('plus-times');
    const { data: plTimes } = await client(
      `admin-gc/visits/plus-times?x&${teeTime ? `?teeTime=${teeTime}` : ''}${
        golfclubId ? `golfclubId=${golfclubId}` : ''
      }`
    );
    setPlusTimes(plTimes?.strTimes || []);
    setInProgress(null);
  };

  const fetchVisits = async () => {
    setInProgress('visits');
    const { data: vsts } = await client(`admin-gc/visits?golfclubId=${golfclubId}`);
    setVisits(vsts);
    setInProgress(null);
  };

  const onCardNrChange = async (value, id) => {
    if (inProgress === 'save') return true;

    const cardNumber = `cardNumber-${id}`;
    const userName = `userName-${id}`;
    setInProgress(cardNumber);
    setValue(userName, '');

    let teeTime = '';
    if (date) {
      teeTime = format(date, 'yyyy-MM-dd HH:mm');
    }

    const { data: cardData, error: cardError } = await client('admin-gc/visits/validate', {
      body: { cardNr: value, teeTime, golfclubId }
    });

    setInProgress(null);

    if (cardError) {
      return cardError.message;
    }

    if (cardData?.ssn) {
      const dob = String(cardData.ssn).substr(0, 6);
      const userData = `${dob} - ${cardData.firstName} ${cardData.lastName}`;
      setValue(userName, userData);
    }

    return true;
  };

  const onSubmitSearch = async (formData) => {
    setInProgress('search');
    setUsers(null);
    setNoUsersFound(false);

    const { data: usrs } = await client(
      `admin-gc/visits/users?dob=${formData.dob}&firstName=${formData.firstName}`
    );

    setInProgress(null);
    if (usrs) {
      setUsers(usrs);
    } else {
      setNoUsersFound(true);
    }
  };

  const closeSearchView = () => {
    setSearchUser(null);
    setUsers(null);
    setNoUsersFound(false);
  };

  const setCardNr = (cardNr) => {
    setValue(`cardNumber-${searchUser}`, cardNr);
    trigger(`cardNumber-${searchUser}`);
    setSearchUser(null);
  };

  const onDateChange = (newDate) => {
    setDate(newDate);
    setPlusTimes(null);
  };

  const onCalendarClose = async () => {
    if (date) {
      const teeTime = format(date, 'yyyy-MM-dd');
      fetchPlusTimes(teeTime);
    }
  };

  const onSubmit = async (formData) => {
    setError(null);
    setSuccess(null);
    setInProgress('save');

    const data = Object.keys(formData)
      .filter((f) => f?.indexOf('cardNumber') >= 0)
      .map((f) => formData[f])
      .filter((f) => f);

    // data.golfclub = formData.golfclub;

    let teeTime = null;
    if (date) {
      teeTime = format(date, 'yyyy-MM-dd HH:mm:ss');
    }

    const { data: regData, error: regError } = await client('admin-gc/visits/register', {
      body: { cardNrs: data, teeTime, golfclubId }
    });
    // console.log('regData', regData, regError);

    if (regError) {
      setError(regError.message);
    } else {
      setSuccess(regData);
      fetchVisits();
    }
    setInProgress(null);
  };

  const closeSuccessView = () => {
    setSuccess(false);
    setDate(null);
  };

  const deleteVisitConfirmed = async (visit) => {
    setError(null);
    setInProgress('delete');
    const { error: deleteError } = await client(`admin-gc/visits/${visit.id}`, {}, 'DELETE');
    if (!deleteError) {
      fetchVisits();
      setDeleteVisit(null);
    } else {
      setError(deleteError.message);
    }
    setInProgress(null);
  };

  const currentDateTime = new Date();
  const selectedDateIsToday = isSameDay(date, currentDateTime);
  const maxTime = selectedDateIsToday
    ? setHours(currentDateTime, getHours(currentDateTime) + 1)
    : midnight2;

  // console.log('affinities', affinities);
  return (
    <>
      <SimpleHeader name={t('visits.title')} />
      <Container className="mt--6" fluid>
        <Row>
          <Col lg="8">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('visits.sub-title')}</h3>
                </CardHeader>
                <CardBody>
                  {/* <Form> */}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                      <Label>{t('default.golfclub')}</Label>
                      {affinities ? (
                        <Select name="golfclub" register={register}>
                          {affinities.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <GInput name="golfclub" register={register} readonly />
                      )}
                    </FormGroup>
                    {[...Array(nrOfCards).keys()]
                      .map((c) => c + 1)
                      .map((c) => {
                        const cardName = `cardNumber-${c}`;
                        const userName = `userName-${c}`;
                        return (
                          <Row form key={cardName}>
                            <Col sm={5}>
                              <FormGroup>
                                <Label>{t('default.card-number')}</Label>
                                <GInput
                                  name={cardName}
                                  register={register}
                                  type="tel"
                                  rules={{
                                    required: true,
                                    pattern: /^\d{8}$/,
                                    validate: (value) => onCardNrChange(value, c)
                                  }}
                                  error={errors[cardName]?.message || !!errors[cardName]}
                                  loading={inProgress === cardName}
                                />
                                <Button
                                  color="link"
                                  className="small pl-0"
                                  style={{ textAlign: 'left' }}
                                  onClick={() => setSearchUser(c)}
                                >
                                  {t('visits.search-user')}
                                </Button>
                              </FormGroup>
                            </Col>
                            <Col sm={6}>
                              <FormGroup>
                                <Label>{t('visits.user')}</Label>
                                <GInput name={userName} readonly register={register} />
                              </FormGroup>
                            </Col>
                            <Col
                              sm={1}
                              className="d-flex align-items-center"
                              style={{ marginTop: '-15px' }}
                            >
                              {c > 1 ? (
                                <Button
                                  color="link"
                                  onClick={() => setNrOfCards(nrOfCards - 1)}
                                  className="only-icon my-0"
                                >
                                  <DashCircle size={25} />
                                </Button>
                              ) : (
                                <Button
                                  color="link"
                                  onClick={() => setNrOfCards(nrOfCards + 1)}
                                  className="only-icon my-0"
                                >
                                  <PlusCircle size={25} />
                                </Button>
                              )}
                            </Col>
                          </Row>
                        );
                      })}
                    <FormGroup>
                      <Label>{t('default.date')}</Label>
                      <DatePicker
                        locale={dateLocale}
                        dateFormat="Pp"
                        showTimeSelect
                        timeIntervals={1}
                        minDate={subDays(currentDateTime, 14)}
                        maxDate={currentDateTime}
                        minTime={midnight}
                        maxTime={maxTime}
                        selected={date}
                        onChange={onDateChange}
                        onCalendarClose={onCalendarClose}
                        className="form-control"
                        placeholderText={t('visits.leave-empty')}
                      />
                    </FormGroup>

                    {error && <Alert color="danger">{error.message || error}</Alert>}

                    <FormGroup className="text-align-right mt-3">
                      <Button type="submit" color="success" disabled={!!inProgress}>
                        {inProgress === 'save' && <Spinner size="sm" className="mr-2" />}
                        {t('visits.registration', 'Registrera besöket')}
                      </Button>
                    </FormGroup>
                  </form>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div
                      className="icon icon-shape bg-gradient-success text-white rounded-circle mb-1"
                      style={{ width: '35px', height: '35px', padding: '6px' }}
                    >
                      <Clock />
                    </div>
                    <h5 className="h3 ml-2">{t('visits.plus-times')}</h5>
                  </div>
                  <CardText className="mt-1">
                    <span className="mr-3">
                      {t('visits.plus-times-exist')}{' '}
                      {date ? `för ${format(date, 'P', { locale: sv })}` : t('default.today')}
                    </span>
                    {inProgress === 'plus-times' && <Spinner />}
                    {plusTimes?.map((p) => (
                      <Badge
                        key={p}
                        size="lg"
                        color="success"
                        className="mr-2"
                        style={{ fontSize: '1rem' }}
                      >
                        {p}
                      </Badge>
                    ))}
                  </CardText>
                </CardBody>
              </Card>
            </div>
          </Col>
          <Col lg="4">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('default.restr')}</h3>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-row align-items-center">
                    <i className="ni ni-bold-right" />
                    {restrictions?.days === 1
                      ? t('default.restr-all-days')
                      : t('default.restr-weekdays')}
                    {restrictions?.weekRest &&
                      `, ${t('default.restr-not-weeks')} ${restrictions?.weekRest}`}
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('visits.quick-links')}</h3>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-row align-items-center">
                    <i className="ni ni-bold-right" />
                    <Link className="small" to="/golfclub-admin/sales">
                      {t('visits.sell-golfhaftet')}
                    </Link>
                  </div>
                  <div className="d-flex flex-row align-items-center mt-2">
                    <i className="ni ni-bold-right" />
                    <Link to={`${plusUrl}`} target="_blank" className="small">
                      <span className="nav-link-text">{t('default.brand')} PLUS</span>
                    </Link>
                    {/* <Link className="small" to={{ plusUrl }}>
                      Lägg ut Golfamore PLUS tider
                    </Link> */}
                  </div>
                  <div className="d-flex flex-row align-items-center mt-2">
                    <i className="ni ni-bold-right" />
                    <Link className="small" to="/golfclub-admin/sales">
                      {t('default.ongoing-campaign')}
                    </Link>
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('visits.latest-visits')}</h3>
                </CardHeader>
                <CardBody>
                  <div
                    className="timeline timeline-one-side"
                    data-timeline-axis-style="dashed"
                    data-timeline-content="axis"
                  >
                    {inProgress === 'visits' && <Spinner />}
                    {visits?.map((v) => (
                      <div className="timeline-block" key={v.id}>
                        <span className="timeline-step badge-success">
                          <i className="ni ni-bold-right" />
                        </span>
                        <div className="timeline-content">
                          <small className="text-muted font-weight-bold">{v.teeTime}</small>
                          <Button
                            color="link"
                            className="m-0 p-0 pl-2"
                            onClick={() => setDeleteVisit(v)}
                          >
                            <XCircle color="red" />
                          </Button>
                          <h5 className="mt-1 mb-0">
                            {v.firstname} {v.lastname}
                          </h5>
                          <div className="mt-1">
                            {v.registrationType === 'APP' && (
                              <Badge color="success" pill>
                                <Phone />
                              </Badge>
                            )}
                            {v.plus > 0 && (
                              <Badge color="success" pill>
                                <PlusCircle />
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {success && (
        <SweetAlerts
          type="success"
          title={t('visits.visit-registrated')}
          confirmBtnText={t('default.close')}
          onConfirm={closeSuccessView}
        >
          <Table className="align-items-center mt-2" responsive>
            <thead className="thead-light">
              <tr>
                <th>{t('default.card-number')}</th>
                <th>{t('default.name')}</th>
                <th>{t('visits.plus-round')}</th>
              </tr>
            </thead>
            <tbody>
              {success.map((v) => (
                <tr key={v.cardNr}>
                  <td>{v.cardNr}</td>
                  <td>
                    {v.user?.firstName} {v.user?.lastName}
                  </td>
                  <td>{v.plusTimeId ? <PlusCircle /> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </SweetAlerts>
      )}

      {searchUser && (
        <Modal isOpen centered size="lg">
          <form onSubmit={handleSubmitSearch(onSubmitSearch)}>
            <ModalHeader>
              <Search className="mr-2" />
              {t('visits.search-user')}
            </ModalHeader>
            <ModalBody className="pt-0">
              <FormGroup className="mb-2">
                <Label>
                  {t('default.date-of-birth')}{' '}
                  <span className="small">({t('default.date-of-birth-help')})</span>
                </Label>
                <GInput
                  name="dob"
                  register={registerSearch}
                  type="tel"
                  rules={{
                    required: true
                  }}
                  error={errorsSearch.dob}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label>{t('visits.firstname', 'Förnamn')}</Label>
                <GInput
                  name="firstName"
                  register={registerSearch}
                  rules={{
                    required: true
                  }}
                  error={errorsSearch.firstName}
                />
              </FormGroup>
              {users && (
                <Table className="align-items-center mt-3" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th>{t('default.name')}</th>
                      <th>{t('visits.city')}</th>
                      <th>{t('default.card-number')}</th>
                      <th>PLUS</th>
                      <th> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>
                          {u.firstName} {u.lastName}
                        </td>
                        <td>{u.city}</td>
                        <td>{u.cardNr}</td>
                        <td>{u.cardPlus && 'J'}</td>
                        <td>
                          <Button size="sm" color="default" onClick={() => setCardNr(u.cardNr)}>
                            {t('visits.use')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {noUsersFound && (
                <Alert color="danger" className="mt-3">
                  {t('visits.user-not-found', 'En användare kunde inte hittas')}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="submit" color="success">
                {inProgress === 'search' && <Spinner size="sm" className="mr-2" />}
                {t('visits.search-user')}
              </Button>
              <Button color="danger" onClick={closeSearchView}>
                {t('default.close')}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      )}

      {deleteVisit && (
        <Modal isOpen centered>
          <ModalHeader>
            <XCircle className="mr-2" />
            {t('visits.delete-visit', 'Radera besök')}?
          </ModalHeader>
          <ModalBody className="pt-0 pb-0">
            <p>
              {t(
                'visits.delete-visit-confirm',
                'Vänligen bekräfta att ni vill radera följande besök.'
              )}
              <br />
              {t('default.card-number')}: {deleteVisit.cardNr}
              <br />
              {t('default.name')}: {deleteVisit.firstname} {deleteVisit.lastname}
            </p>
            {error && <Alert color="danger">{error}</Alert>}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => deleteVisitConfirmed(deleteVisit)}
              disabled={inProgress === 'delete'}
            >
              {inProgress === 'delete' && <Spinner size="sm" className="mr-2" />}
              {t('visits.delete-visit')}
            </Button>
            <Button color="default" onClick={() => setDeleteVisit(null)}>
              {t('default.close')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default Visits;
